import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { OverTheAirUpdateApiService } from '@dc-api/over-the-air-update.api.service';
import { SmartDeviceFirmware } from '@dc-core/dc-backend/dc-classes';
import { SmartDeviceFirmwareDeploymentResponse } from '@dc-core/dc-backend/dc-responses';
import { ApiResponse } from 'openapi-typescript-fetch';

import { SmartDeviceDeploymentProgress, SmartDeviceDeploymentState } from '../../models/smart-devices.models';
import { OmniCommunicationService } from './omni-communication.service';
import { OmniStateService } from './omni-state.service';

@Injectable({
    providedIn: 'root',
})
export class OverTheAirUpdateService {
    public deploymentState: WritableSignal<SmartDeviceDeploymentState> = signal(null);
    public deploymentProgress: WritableSignal<SmartDeviceDeploymentProgress> = signal(null);

    private checkingForUpdates: WritableSignal<boolean> = signal(false);
    public isCheckingForUpdates: Signal<boolean> = computed(() => this.checkingForUpdates());

    private firmwareUpdate: WritableSignal<SmartDeviceFirmware> = signal(null);
    public currentFirmwareUpdate: Signal<SmartDeviceFirmware> = computed(() => this.firmwareUpdate());

    public updatingDevice: WritableSignal<boolean> = signal(null);
    public isUpdating: Signal<boolean> = computed(
        () => !this.deploymentState()?.success && !this.deploymentState()?.failure && this.updatingDevice()
    );

    private overTheAirUpdateApiService: OverTheAirUpdateApiService = inject(OverTheAirUpdateApiService);
    private omniCommunicationService: OmniCommunicationService = inject(OmniCommunicationService);
    private omniStateService: OmniStateService = inject(OmniStateService);

    public checkForUpdates(smartDeviceId: number): void {
        this.checkingForUpdates.set(true);
        this.overTheAirUpdateApiService
            .checkForSmartDeviceUpdates({ smartDeviceId })
            .then((res: ApiResponse<SmartDeviceFirmware>) => {
                this.checkingForUpdates.set(false);

                const firmware = !res.data?.id ? null : res.data;
                this.firmwareUpdate.set(firmware);
            });
    }

    public setDeploymentState(state: any): void {
        this.deploymentState.set(state);
    }

    public setDeploymentProgress(progress: string): void {
        this.deploymentProgress.set(progress as SmartDeviceDeploymentProgress);
    }

    public updateDevice(smartDeviceId: number): Promise<ApiResponse<SmartDeviceFirmwareDeploymentResponse>> {
        this.updatingDevice.set(true);

        return this.overTheAirUpdateApiService.updateSmartDeviceFirmware({
            smartDeviceId,
        });
    }

    public repairDevice(smartDeviceId: number): Promise<ApiResponse<SmartDeviceFirmwareDeploymentResponse>> {
        this.updatingDevice.set(true);

        return this.overTheAirUpdateApiService.repairSmartDeviceFirmware({
            smartDeviceId,
        });
    }

    public rollback(smartDeviceId: number): Promise<ApiResponse<SmartDeviceFirmwareDeploymentResponse>> {
        this.updatingDevice.set(true);

        return this.overTheAirUpdateApiService.rollbackSmartDeviceFirmware({
            smartDeviceId,
        });
    }

    public override(
        smartDeviceId: number,
        firmwareId: number
    ): Promise<ApiResponse<SmartDeviceFirmwareDeploymentResponse>> {
        this.updatingDevice.set(true);

        return this.overTheAirUpdateApiService.overrideSmartDeviceFirmware({
            smartDeviceId,
            smartDeviceFirmwareId: firmwareId,
        });
    }

    public resetState() {
        this.setDeploymentState(null);
        this.setDeploymentProgress(null);
        this.updatingDevice.set(false);
    }

    public watchDeploymentStatus(smartDeviceId: number, deploymentId: string) {
        if (this.deploymentState()?.success || this.deploymentState()?.failure) {
            return;
        } else {
            this.overTheAirUpdateApiService
                .getSmartDeviceFirmwareStatus({
                    smartDeviceId,
                    deploymentId,
                })
                .then((res) => {
                    this.setDeploymentState(res.data.statistics.status);
                    this.setDeploymentProgress(res.data.status);

                    if (res.data.status !== 'finished') {
                        setTimeout(() => {
                            this.watchDeploymentStatus(smartDeviceId, deploymentId);
                        }, 10000);
                    } else {
                        this.finishUpdate(smartDeviceId);
                    }
                });
        }
    }

    public confirmUpdate(smartDeviceId: number): Promise<ApiResponse<SmartDeviceFirmware>> {
        return this.overTheAirUpdateApiService.confirmSmartDeviceFirmwareUpdate({
            smartDeviceId,
        });
    }

    private finishUpdate(smartDeviceId: number) {
        if (this.deploymentState().success) {
            this.omniCommunicationService.rebootOmni();
            console.log('Update succeeded!');
        } else if (this.deploymentState().failure) {
            console.warn('Update failed!');
        } else if (this.deploymentState()['already-installed']) {
            console.warn('Update already-installed!');
        }

        this.confirmUpdate(smartDeviceId).then((res) => {
            this.checkForUpdates(smartDeviceId);
            this.omniStateService.updateCurrentSmartDevice(() => ({
                firmware: res.data,
            }));
            this.updatingDevice.set(false);
        });
    }
}
